import type { JSX } from 'react';
import { useState } from 'react';

import { Button, Link, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';

import { EndorsementCard } from '../EndorsementCard';

import {
	actionButtonsContainer,
	descriptionContainer,
	endorsementCardContainer,
	sectionContainer,
	titleContainer,
} from './EndorsementSection.styles';

// Mock data - will be replaced with real data from API
const MOCK_ENDORSEMENTS = [
	{
		id: '1',
		name: 'Rob Mahoney',
		title: 'Director of Berkley City Parks',
		description:
			'As a community advocate, I strongly support this petition and the people behind it. This issue affects real lives, and the petition clearly outlines both the problem and a path forward. Change happens when everyday people speak up and leaders listen. I urge decision-makers to engage seriously with this call for action, and I encourage others to sign and share. Collective voices matter—and this is a moment to use them.',
		photoUrl: 'https://via.placeholder.com/66',
		link: '#',
	},
	{
		id: '2',
		name: 'Sarah Johnson',
		title: 'Environmental Scientist',
		description:
			'I have studied environmental issues for over 15 years, and this petition addresses a critical concern that demands immediate attention. The research supporting these claims is solid, and the proposed solutions are both practical and effective. As someone who has dedicated their career to environmental protection, I can confidently say this initiative has the potential to make a real difference.',
		photoUrl: 'https://via.placeholder.com/66',
		link: '#',
	},
	{
		id: '3',
		name: 'Michael Chen',
		title: 'Community Organizer',
		description:
			'Having worked with countless community members on various initiatives, I recognize the importance of this petition. It represents the voices of many who have been affected by this issue. The organizers have done an excellent job of bringing together diverse perspectives and creating a unified call to action. I wholeheartedly endorse this effort.',
		photoUrl: 'https://via.placeholder.com/66',
		link: '#',
	},
];

export function EndorsementSection(): JSX.Element {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const navigate = useNavigate();
	const [showSingleEndorsement, setShowSingleEndorsement] = useState(true);

	const handleAddAnother = () => {
		navigate(`/p/${slug}/dashboard/endorsements/add`);
	};

	const handleViewOnPetition = () => {
		// TODO: Navigate to petition page
		// eslint-disable-next-line no-console
		console.log('View on petition page clicked');
	};

	const handleEdit = () => {
		// TODO: Navigate to edit endorsement page
	};

	const handleRemove = () => {
		// TODO: Show confirmation modal and remove endorsement
	};

	const handleToggleEndorsementView = () => {
		setShowSingleEndorsement((prev) => !prev);
	};

	const endorsementsToDisplay = showSingleEndorsement ? [MOCK_ENDORSEMENTS[0]] : MOCK_ENDORSEMENTS;

	return (
		<div className={sectionContainer()} data-testid="endorsement-section">
			{/* Header */}
			<div className={titleContainer()}>
				<Text variant="heading" size="x-small" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.title" />
				</Text>
			</div>

			{/* Description */}
			<div className={descriptionContainer()}>
				<Text variant="body" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.description" />{' '}
					<Link href="#" variant="secondary" className="underline">
						<Translate value="corgi.petition.edit.endorsements.see_example" />
					</Link>
				</Text>
			</div>

			{/* Endorsement Cards */}
			<div className={endorsementCardContainer()}>
				{endorsementsToDisplay.map((endorsement, index) => {
					const isFirstCard = index === 0;
					const readMoreLabel = showSingleEndorsement ? 'Read full endorsement' : 'View less';

					return (
						<EndorsementCard
							key={endorsement.id}
							name={endorsement.name}
							title={endorsement.title}
							description={endorsement.description}
							photoUrl={endorsement.photoUrl}
							link={endorsement.link}
							onEdit={handleEdit}
							onRemove={handleRemove}
							onReadMore={isFirstCard ? handleToggleEndorsementView : undefined}
							readMoreLabel={isFirstCard ? readMoreLabel : undefined}
							className="bg-background-base border-stroke-weak rounded-lg border"
						/>
					);
				})}
			</div>

			{/* Action Buttons */}
			<div className={actionButtonsContainer()}>
				<Button onClick={handleAddAnother} variant="secondary" data-testid="add-another-endorsement-button">
					<Translate value="corgi.petition.edit.endorsements.add_another_button" />
				</Button>
				<Button onClick={handleViewOnPetition} variant="tertiary" data-testid="view-on-petition-button">
					<Translate value="corgi.petition.edit.endorsements.view_on_petition_button" />
				</Button>
			</div>
		</div>
	);
}
